import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Home, Autorenew, PersonOff, MergeType, Elderly, HomeWork, FamilyRestroom, AccountBalance } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const ServicesWrapper = styled.div`
  padding: 4rem 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const ServicesHeader = styled.div`
  text-align: center;
  margin-bottom: 4rem;
`;

const ServicesTitle = styled.h1`
  color: #2F4F4F;
  font-size: 2.5rem;
  margin-bottom: 1rem;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    right: 50%;
    transform: translateX(50%);
    width: 100px;
    height: 3px;
    background: #2F4F4F;
  }
`;

const ServicesSubtitle = styled.p`
  color: #666;
  font-size: 1.2rem;
  max-width: 600px;
  margin: 2rem auto;
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 2rem;
  margin-top: 2rem;
`;

const ServiceCard = styled(motion.div)`
  background: white;
  padding: 2rem;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  text-align: center;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 6px 12px rgba(0, 0, 0, 0.15);
  }
`;

const ServiceIcon = styled.div`
  color: #2F4F4F;
  font-size: 2.5rem;
  margin-bottom: 1.5rem;
  
  svg {
    font-size: 3rem;
  }
`;

const ServiceTitle = styled.h3`
  color: #2F4F4F;
  font-size: 1.5rem;
  margin-bottom: 1rem;
`;

const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
`;

const ServiceButton = styled(motion.button)`
  background: #2F4F4F;
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: #1c2f2f;
  }
`;

const Services = ({ type }) => {
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const navigate = useNavigate();

  const services = [
    {
      icon: <Home />,
      title: 'משכנתא חדשה',
      description: 'ליווי מקצועי ומקיף בתהליך לקיחת המשכנתא הראשונה שלכם, כולל השוואת מסלולים והתאמה אישית.',
      path: '/services/new'
    },
    {
      icon: <Autorenew />,
      title: 'מחזור משכנתא',
      description: 'בדיקה מקיפה של תנאי המשכנתא הקיימת והצעת מסלול מחזור משתלם יותר עבורכם.',
      path: '/services/refinance'
    },
    {
      icon: <PersonOff />,
      title: 'משכנתא למסורבים',
      description: 'פתרונות יצירתיים ומותאמים אישית למי שנתקל בקשיים בקבלת משכנתא מהבנקים.',
      path: '/services/rejected'
    },
    {
      icon: <MergeType />,
      title: 'איחוד הלוואות',
      description: 'איחוד כל ההלוואות שלכם למשכנתא אחת עם החזר חודשי נוח יותר ותנאים משופרים.',
      path: '/services/consolidation'
    },
    {
      icon: <Elderly />,
      title: 'משכנתא הפוכה',
      description: 'פתרון ייחודי לבני הגיל השלישי המאפשר לקבל הכנסה חודשית קבועה מהנכס שבבעלותכם.',
      path: '/services/reverse'
    },
    {
      icon: <HomeWork />,
      title: 'גרירת משכנתא',
      description: 'העברת המשכנתא הקיימת לנכס חדש תוך שמירה על התנאים הטובים שהשגתם.',
      path: '/services/transfer'
    },
    {
      icon: <FamilyRestroom />,
      title: 'ייעוץ לכלכלת המשפחה',
      description: 'תכנון פיננסי מקיף למשפחה, כולל ניהול תקציב, חיסכון והתייעלות כלכלית.',
      path: '/services/family'
    },
    {
      icon: <AccountBalance />,
      title: 'ייעוץ אשראי',
      description: 'ייעוץ מקצועי לשיפור דירוג האשראי שלכם והתאמת פתרונות אשראי מיטביים.',
      path: '/services/credit'
    }
  ];

  return (
    <ServicesWrapper>
      <ServicesHeader>
        <motion.div {...fadeIn}>
          <ServicesTitle>השירותים שלנו</ServicesTitle>
          <ServicesSubtitle>
            אנו מציעים מגוון רחב של פתרונות פיננסיים המותאמים לצרכים הייחודיים שלכם,
            עם דגש על מקצועיות, שקיפות ושירות אישי
          </ServicesSubtitle>
        </motion.div>
      </ServicesHeader>

      <ServicesGrid>
        {services.map((service, index) => (
          <ServiceCard
            key={index}
            as={motion.div}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            whileHover={{ y: -10, transition: { duration: 0.2 } }}
          >
            <ServiceIcon>{service.icon}</ServiceIcon>
            <ServiceTitle>{service.title}</ServiceTitle>
            <ServiceDescription>{service.description}</ServiceDescription>
            <ServiceButton
              as={motion.button}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate(service.path)}
            >
              למידע נוסף
            </ServiceButton>
          </ServiceCard>
        ))}
      </ServicesGrid>
    </ServicesWrapper>
  );
};

export default Services; 